import React, { useState } from 'react';
import { TextField, Button, Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { QueryInputProps } from '../../../types/types';
import { raiseRequest } from '../../../services/checkalter';
import { getLob } from '../../../services/authenticationService';
import { GenericPopUp } from '../../popup/genericPopUp';
import { popupType } from '../../../types/types';
import Header from "../../../components/dms_common/Header";
import './QueryInput.css';

export function QueryInput(props: QueryInputProps) {
  const [query, setQuery] = useState('');
  const [openGenericModal, setOpenGenericModal] = useState(false);
  const [popupType, setPopupType] = useState<popupType>("Alert");
  const [genericMessage, setGenericMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [context, setContext] = useState("");

  const openPopup = (message: string, type: popupType) => {
    setPopupType(type);
    setGenericMessage(message);
    setOpenGenericModal(true);
  };

  const handleDialogOpen = () => {
    if (!props.selectedOptionApprover) {
      openPopup("Please select an approver before inserting records", "Alert");
      return;
    }
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setContext("");
  };

  const handleRequestApproval = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        changes: [{
          id: Date.now().toString(),
          query: query
        }],
        metadata: {
          username: props.userEmail,
          context: context,
          lob: getLob(),
          approver: props.selectedOptionApprover
        }
      };
      
      const response = await raiseRequest(payload);
      if (response.features[0] === "Changes Executed Successfully") {
        openPopup("Request raised", "Success");
        setQuery("");
        handleDialogClose();
      }
    } catch (error) {
      openPopup("Failed to commit changes. Please try again.", "Alert");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContext(e.target.value);
  };

  return (
    <div className='query-container-checkmate'>
      <GenericPopUp
        message={genericMessage}
        type={popupType}
        openGenericModal={openGenericModal}
        setOpenGenericModal={setOpenGenericModal}
      />
      <header className="top-heading" style={{ fontSize: "19px", marginBottom: "1rem" }}>
        <Header title={"Custom Query"} />
      </header>
      <div className='query-input-container'>
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
        >
          <TextField
            label="Enter your custom query"
            variant="outlined"
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            multiline
            minRows={3}
            sx={{ 
              textarea: { 
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: '14px'
              }
            }}
          />
          <Box display="flex" justifyContent="center">
            <button
              onClick={handleDialogOpen}
              style={{
                backgroundColor: '#00C6B1',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '10px 16px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: isSubmitting || !query.trim() ? 'not-allowed' : 'pointer',
                opacity: isSubmitting || !query.trim() ? 0.5 : 1,
                transition: 'opacity 0.3s ease',
              }}
              disabled={isSubmitting || !query.trim()}
            >
              {isSubmitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CircularProgress size={18} color="inherit" />
                  Processing
                </Box>
              ) : (
                'Request Approval'
              )}
            </button>
          </Box>
        </Box>
      </div>

      <Dialog
        open={openDialog}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleDialogClose();
          }
        }}
        PaperProps={{
          style: {
            width: '500px',
            height: '320px',
            borderRadius: '10px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '24px',
            gap: '24px'
          }
        }}
      >
        <DialogTitle style={{
          padding: 0,
          margin: 0,
          color: "#252525",
          fontWeight: 500,
          fontSize: '20px',
          textAlign: 'center',
          width: '100%'
        }}>
          Enter reason for query request
        </DialogTitle>

        <DialogContent style={{
          padding: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <TextField
            multiline
            rows={2}
            variant="outlined"
            value={context}
            onChange={handleContextChange}
            placeholder="Please mention a reason..."
            sx={{
              width: '90%',
              '& .MuiOutlinedInput-root': {
                '& textarea': {
                  minHeight: '50px'
                }
              }
            }}
          />
        </DialogContent>

        <DialogActions style={{
          padding: 0,
          gap: '16px'
        }}>
          <Button
            onClick={handleDialogClose}
            sx={{
              backgroundColor: '#ffffff',
              color: '#c44c36',
              border: '1px solid #c44c36',
              borderRadius: '4px',
              minWidth: '120px',
              height: '36px',
              fontWeight: 550,
              fontSize: '14px',
              textTransform: 'none',
              '&.Mui-disabled': {
                color: '#c44c36',
                borderColor: '#c44c36',
                cursor: 'not-allowed',
                opacity: 0.6,
                backgroundColor: '#ffffff',
              },
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRequestApproval}
            sx={{
              backgroundColor: '#00c6b1',
              color: '#ffffff',
              borderRadius: '4px',
              minWidth: '120px',
              height: '36px',
              fontWeight: 550,
              fontSize: '14px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#00b3a1',
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                backgroundColor: '#00b3a1',
                color: '#ffffff',
                cursor: 'not-allowed',
                opacity: '0.5'
              }
            }}
            disabled={!context.trim() || isSubmitting}
          >
            {isSubmitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircularProgress size={18} color="inherit" />
                Submitting
              </Box>
            ) : (
              'Submit'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}